import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MenuStructureService } from '@core/layouts/menu-structure.service';
import { NavItemAccessService } from '@core/layouts/nav-item-access.service';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NavItemComponent } from '../nav-item/nav-item.component';
import { SectionTitleComponent } from '../section-title/section-title.component';

const COLLAPSE_SNAPBACK_DELAY_MS = 250;
const OVERFLOW_THRESHOLD_PX = 1;

@Component({
  selector: 'pm-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [
    TranslateModule,
    InlineSVGModule,
    NavItemComponent,
    SectionTitleComponent,
  ],
  host: {
    class: 'h-[calc(100vh-56px)] fixed top-14 left-0 shadow bg-white z-[999]',
  },
})
export class SidebarComponent implements AfterViewInit {
  protected menuStructureService = inject(MenuStructureService);
  protected navItemAccessService = inject(NavItemAccessService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly viewport =
    viewChild.required<ElementRef<HTMLElement>>('viewport');

  protected readonly isHovered = signal(false);
  protected readonly canScrollUp = signal(false);
  protected readonly canScrollDown = signal(false);

  private resizeObserver: ResizeObserver | null = null;
  private collapseTimer: ReturnType<typeof setTimeout> | null = null;

  ngAfterViewInit(): void {
    const el = this.viewport().nativeElement;

    this.resizeObserver = new ResizeObserver(() => this.updateFades());
    this.resizeObserver.observe(el);

    this.destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();
      if (this.collapseTimer !== null) {
        clearTimeout(this.collapseTimer);
      }
    });

    this.updateFades();
  }

  protected onEnter(): void {
    if (this.collapseTimer !== null) {
      clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    }
    this.isHovered.set(true);
  }

  protected onLeave(): void {
    this.isHovered.set(false);
    this.collapseTimer = setTimeout(() => {
      this.viewport().nativeElement.scrollTop = 0;
      this.updateFades();
      this.collapseTimer = null;
    }, COLLAPSE_SNAPBACK_DELAY_MS);
  }

  protected onScroll(): void {
    this.updateFades();
  }

  private updateFades(): void {
    const el = this.viewport().nativeElement;
    const overflows = el.scrollHeight - el.clientHeight > OVERFLOW_THRESHOLD_PX;

    this.canScrollUp.set(overflows && el.scrollTop > OVERFLOW_THRESHOLD_PX);
    this.canScrollDown.set(
      overflows &&
        el.scrollHeight - el.scrollTop - el.clientHeight >
          OVERFLOW_THRESHOLD_PX,
    );
  }
}
